import { deleteUser, updateUser } from "src/api/v1.6/user";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteSession, getSession } from "src/api/v1.6/auth/session";
import { ONE_DAY_IN_MILLISECONDS } from "src/utils/conversions/time";
import { ApiError } from "src/api/ApiError";
import type { UserProfile } from "src/api/v1.6/schema/UserProfile";
import { logFetchError } from "./utils/log";
import { clearLocalSessionState, isLoggedIn } from "./utils/session";

export const authKeys = {
  all: ["auth"] as const,
  user: () => [...authKeys.all, "user"] as const,
};

export function useUserProfile<T = UserProfile>(
  select?: (data: UserProfile) => T,
) {
  return useQuery({
    queryKey: authKeys.user(),
    staleTime: ONE_DAY_IN_MILLISECONDS,
    enabled: isLoggedIn(),
    async queryFn() {
      try {
        const session = await getSession();
        // We ignore the sessionId in the response and just return the user.
        // This probably isn't ideal, we could probably be calling the
        // getUser endpoint instead.
        return session.user;
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          clearLocalSessionState();
        } else {
          logFetchError(error);
        }
        throw error;
      }
    },
    select,
  });
}

export function useMutateUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateUser,
    onSuccess(data) {
      queryClient.setQueryData(authKeys.user(), data);
    },
    onError(error) {
      logFetchError(error);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteSession,
    onMutate() {
      queryClient.removeQueries({ queryKey: authKeys.all });
    },
    onSuccess() {
      clearLocalSessionState();
    },
    onError: (error) => {
      logFetchError(error);
    },
  });
}

export function useDeleteUserProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn() {
      await deleteUser();
      await deleteSession();
    },
    onSuccess() {
      queryClient.removeQueries({ queryKey: authKeys.all });
      clearLocalSessionState();
    },
    onError: (error) => {
      logFetchError(error);
    },
  });
}
